import { tokenManager } from "lib/tokenManager";
import { trpc } from "lib/trpc";

/**
 * Rendered height in px (py-2 + text-sm line box + 1px bottom border).
 *
 * Exported because UpdateBanner (PRTL-1360) is also `fixed top-0 z-100` and
 * mounts in a different tree (App.tsx vs PortalLayout), so the two cannot be
 * stacked by a shared flex container — it offsets itself by this instead.
 * Keep in sync if this bar's padding or text size changes.
 */
export const MASQUERADE_BANNER_HEIGHT_PX = 37;

/**
 * PRTL-1348 masquerade: persistent full-width warning bar shown on every
 * screen while a compass admin is viewing the app as another user. The
 * server derives the state from the token; this banner is informational —
 * mutations are blocked server-side regardless.
 */
export const MasqueradeBanner = () => {
    const { data: user } = trpc.users.me.useQuery(undefined, {
        enabled: tokenManager.isMasquerading(),
    });
    const logoutMutation = trpc.auth.logout.useMutation();

    if (!tokenManager.isMasquerading()) return null;

    const handleExit = () => {
        // Revoke the masquerade session server-side, then drop the
        // sessionStorage token — the admin's own token (if any) takes over.
        logoutMutation.mutate(undefined, {
            onSettled: () => {
                tokenManager.clearMasquerade();
                window.location.assign("/");
            },
        });
    };

    const targetName = user ? `${user.firstName} ${user.lastName}` : "another user";
    const actor = user?.masquerade?.actor;

    return (
        <div className="fixed inset-x-0 top-0 z-100 flex items-center justify-center gap-3 border-b border-amber-300 bg-amber-100 px-4 py-2 text-sm text-amber-900">
            <span className="font-medium">
                Viewing as {targetName} (read-only)
                {actor ? ` — you are logged in as ${actor.name}` : ""}
            </span>
            <button
                onClick={handleExit}
                disabled={logoutMutation.isPending}
                className="rounded-md border border-amber-400 bg-white px-2.5 py-1 text-xs font-semibold text-amber-900 hover:bg-amber-50 disabled:opacity-60"
            >
                Exit masquerade
            </button>
        </div>
    );
};
